import AppRecord from 'stores/models/modelFiles/AppRecord.js'
import { defineStore } from 'pinia'
// import pinia from "stores";
import User from './models/modelFiles/User.js';
// Now read all controllers and add data from them
import { state, getters, actions } from 'stores/controllers/index.js'
import { state as eState, getters as eGetters, actions as eActions } from 'stores/modules/store-enums.js'

let rootState = {
  state_current_user: { id: undefined, roles: [] },
  users: [],
  applicationLogSource: null,
  searchDialogShown: false,
  activeSearchTab: null,
  loginRedirectPath: null,
  documentNoteSource: null,
  reportData: null,
  active_consultants: null,
  ...state,
  ...eState
}

let statesVar = () => (rootState)

let gettersVar = {
  current_user(state) {
    // Point to state_Current_user, which is set along localStorage, this
    // ensures re-run, fetch of localStorage upon set user. Otherwise the getter
    // will return last value.
    state.state_current_user;
    let usr = JSON.parse(localStorage.getItem('current_user')) || { id: undefined, roles: [] };
    if (usr.id) {
      let res = state.users.find(us => us && us.id === usr.id);
      if (res) {
        return res;
      }
    }
    return new User(usr);
  },
  current_user_is_employee(state) {
    const user = state.current_user;
    return user.roles.includes("employee") || user.roles.includes("aep");
  },
  current_user_is_investor(state) {
    const user = state.current_user;
    return user.roles.includes("client");
  },
  sign_in_path(state) {
    const user = state.current_user;
    if (user.roles.includes("family") && !user.roles.includes("employee") && !user.roles.includes("aep")) {
      return { path: "/employee/crm/reports" };
    } else if (user.roles.includes("norway") && !user.roles.includes("employee") && !user.roles.includes("aep")) {
      return { path: "/employee/markets/average_acquisition_costs" };
    }
    if (user.roles.includes("employee") || user.roles.includes("aep")) {
      return { path: "/employee/crm/clients" };
    } else if (user.roles.includes("client")) {
      return { path: "/investor/home" };
    } else {
      return { path: "/" };
    }
  },
  clients(state){
    return state.people.filter(per => per.type === 'Client')
  },
  bank_contacts(state){
    return state.people.filter(per => per.type === 'BankContact')
  },
  employees(state){
    return state.people.filter(per => per.type === 'Employee')
  },
  advisable(state){
    return state.people.filter(per => per.type != 'BankContact')
  },
  getActiveConsultants(state) {
    return state.active_consultants
  },
  entitiesById(state) {
    return new Map(state.entities.map(e => [e.id, e]));
  },
  accountsById(state) {
    return new Map(state.accounts.map(a => [a.id, a]));
  },
  peopleById(state) {
    return new Map(state.people.map(p => [p.id, p]));
  },
  usersByPersonId(state) {
    return new Map(state.users.filter(u => u.person_id != null).map(u => [u.person_id, u]));
  },
  ownershipsByPersonId(state) {
    const map = new Map();
    for (const own of state.ownerships) {
      if (!map.has(own.person_id)) map.set(own.person_id, []);
      map.get(own.person_id).push(own);
    }
    return map;
  },
  personContactInformationsById(state) {
    const map = new Map();
    for (const cc of state.contact_informations) {
      if (cc.contactable_type !== 'Person') continue;
      if (!map.has(cc.contactable_id)) map.set(cc.contactable_id, []);
      map.get(cc.contactable_id).push(cc);
    }
    return map;
  },
  riskAssessmentsByPersonId(state) {
    const map = new Map();
    for (const r of state.risk_assessments) {
      if (!map.has(r.person_id)) map.set(r.person_id, []);
      map.get(r.person_id).push(r);
    }
    return map;
  },
  personConsultantsByPersonId(state) {
    const map = new Map();
    for (const rel of state.person_consultants) {
      if (!map.has(rel.person_id)) map.set(rel.person_id, []);
      map.get(rel.person_id).push(rel);
    }
    return map;
  },
  ...getters,
  ...eGetters
}

let actionsVar = 
{
  GET_INVESTOR_INVESTMENTS(){
    return ("GET_INVESTMENTS", {
      where: {
        entity_id: rootState.entities.map(ent => ent.id),
      },
      scopes: {
        primary: `${getters.start_date}..${getters.end_date}`,
      }
    })
  },
  SET_CURRENT_USER(payload) {
    // Ensure to set state_current_user upon setting localStorage, the getter
    // will point to state_current_user, thus refetch localStorage when changed
    // in state. localStorage is needed to persist current_user after page
    // refresh or browser shutdown
    this.state_current_user = payload;
    localStorage.setItem("current_user", JSON.stringify(payload));
  },
  showApplicationLog(payload) {
    this.applicationLogSource = payload;
  },
  showDocumentNoteForm(payload) {
    this.documentNoteSource = payload;
  },
  openSearchDialog({ tabId }) {
    this.activeSearchTab = tabId;
    this.searchDialogShown = true;
  },
  setActiveSearchTab(tabId) {
    this.activeSearchTab = tabId;
  },
  closeSearchDialog() {
    this.searchDialogShown = false;
  },
  // Named apart from the `loginRedirectPath` state key it writes - an action and
  // a state entry cannot share a name on the same store.
  SET_LOGIN_REDIRECT_PATH(payload) {
    this.loginRedirectPath = payload;
  },
  reportingData(payload) {
    this.reportData = payload;
  },
  controllerForModel(modelName) {
    // Get the relevant controller
    let controllerName = modelName + "sController";
    if (!this.controllers[controllerName]) {
      controllerName = modelName + "Controller";
      if (!this.controllers[controllerName]) {
        controllerName = modelName;
        if (!this.controllers[controllerName]) {

          // Search through all controllers to find by object_name
          const matchingController = Object.values(this.controllers).find(
              (c) => (c.object_name || "").toLowerCase() === modelName.toLowerCase()
            );

          if (matchingController.length > 0) {
            controllerName = matchingController.object_name;
          } else {
            // Throw error
            window.console.error((controllerName || "") + " not found in store");
            return null;
          }
        }
      }
    }

    let controller = this.controllers[controllerName];
    if (!controller) {
      controller = Object.values(this.controllers).find(
        (c) => (c.object_name || "").toLowerCase() == modelName.toLowerCase()
      );
    }
    return controller;
  },
  enumByName(modelName, enumName) {
    let controller = this.controllerForModel(modelName);

    if (controller) {
      let metaData = (controller || {})._description || {};
      if (metaData.enums) {
        let enummy = Object.values(metaData.enums).find(
          (x) => x.name == enumName
        );
        if (enummy && enummy.values) {
          return Object.values(enummy.values);
        }
      }
    }
    return null;
  },
  RESET_STATE(){
    return new Promise((resolve) => {
      rootState.start_date = null
      rootState.end_date = null
      rootState.covmat = []
      rootState.covmat_index = []
      rootState.primary_consultant = undefined
      rootState.secondary_consultant = undefined
      Object.entries(rootState).forEach(([key, value]) => {
        if (Array.isArray(value) && value.some(item => item instanceof AppRecord)) {
          rootState[key] = [];
        } else 
        if (key == 'controllers') {
            Object.entries(value).forEach(([name, controller]) => {
              controller.data = [];
              controller.collected = false;
              controller.setMaxUpdatedAt();
            });
        }
      });
      resolve(true);
    })
  },
  ...actions,
  ...eActions
}

export const useEmployeeStore = defineStore('employee', {
  state: statesVar,
  getters: gettersVar,
  actions: actionsVar
});
